import { ThunkDispatch } from '@reduxjs/toolkit';
import { DiaryRootState } from 'appRoot/rootReducer';
import ServicesLookupService from 'external-services/services-lookup-service';
import { BookingAvailabilityService } from 'external-services/booking-availability-service';
import LayoutsService from 'external-services/layouts-service/layouts.service';
import { IBookingTime, ILayoutMinimalAbstract, ITableItem } from 'shared-types/SharedTypes';
import TableSelectionService from 'external-services/table-selection-service';
import SectionLookupService from 'external-services/section-lookup-service/sectionLookup.service';
import { cloneDeep } from 'lodash';
import { standbyListActions } from 'booking-creator/reducers/standbyList/slice';
import { IBookingInfoAtTime } from 'external-services/booking-availability-service/types';
import { bookingTimesActions } from 'booking-creator/reducers/bookingTimes/slice';
import { errorStateActions, getAccordionDetail } from 'app/reducers/errorState/slice';
import { CONTACT_MSG, PROBLEMS_MSG, REFRESH_MSG } from 'appRoot/app-error-messages';

export const updateStandByTableSeaterOnTimeChange =
  () =>
  async (dispatch: ThunkDispatch<DiaryRootState, {}, any>, getState: () => DiaryRootState) => {
    const {
      displayReducer: { activeBooking },
      scheduleReducer: { selectedSchedule },
      bookingReducer: { couchReducedBookings },
      bookingTimesReducer: { availableTimes },
    } = getState();

    const mutatedAvailableTimes = cloneDeep(availableTimes);

    const matchedService = ServicesLookupService.getServiceFromSchedule(
      selectedSchedule,
      activeBooking.time,
      activeBooking.serviceId
    );
    // create a bookings by table without the current booking so it is a vaild booking
    const bookingsByTable = BookingAvailabilityService.getBookingsByTable(
      couchReducedBookings.filter(({ _id }) => _id !== activeBooking._id) as any
    );

    const layoutsIds = ServicesLookupService.getLayoutIdsFromServices(
      selectedSchedule.services,
      matchedService.sectionLayouts,
      activeBooking.time,
      activeBooking.functionTracker
    );
    let getByLayoutsResult: { tablesArray: ITableItem[]; returnRef: any } = null;
    try {
      getByLayoutsResult = await LayoutsService.getByLayouts(layoutsIds, activeBooking.time);
    } catch (e) {
      console.warn('StandbyListHelper', 'LayoutsService.getByLayouts error', e);
      dispatch(
        errorStateActions.setErrorState({
          errorTitle: 'Tables refresh error',
          errorMessage: `There was an error while refreshing table data. \n\n${PROBLEMS_MSG} ${REFRESH_MSG} \n\n${CONTACT_MSG}`,
          accordionDetail: getAccordionDetail(e),
        })
      );
    }
    const { tablesArray: tables } = getByLayoutsResult;
    let selectedTime: IBookingTime = null;

    // make sure the set booking time has been checked, this is maily for walkin whick can be booked at any time
    // move to isBookingTimeInList function in booking-availability-service of submodule
    const isInList = BookingAvailabilityService.isBookingTimeInList(
      mutatedAvailableTimes as IBookingInfoAtTime[],
      activeBooking.time
    );
    let layouts: ILayoutMinimalAbstract[] = [];
    try {
      layouts = await LayoutsService.getLayouts(layoutsIds, activeBooking.time);
    } catch (e) {
      console.warn('StandbyListHelper', 'LayoutsService.getLayouts error', e);
      dispatch(
        errorStateActions.setErrorState({
          errorTitle: 'Layouts refresh error',
          errorMessage: `There was an error while refreshing layout data. \n\n${PROBLEMS_MSG} ${REFRESH_MSG} \n\n${CONTACT_MSG}`,
          accordionDetail: getAccordionDetail(e),
        })
      );
    }

    const bookedTime = BookingAvailabilityService.checkBookingsAtTime(
      activeBooking.time,
      activeBooking.time,
      selectedSchedule,
      matchedService,
      cloneDeep(layouts),
      Number(activeBooking.duration),
      activeBooking.people,
      cloneDeep(tables),
      cloneDeep(bookingsByTable),
      activeBooking.preferredSectionId,
      null,
      true,
      null,
      null
    );

    if (!isInList) {
      // if the booked time is not in the list check if it is available
      mutatedAvailableTimes.push(bookedTime);
    }

    const selectedBookingTimeFromList = BookingAvailabilityService.getSelectedBookingTimeFromList(
      mutatedAvailableTimes as IBookingInfoAtTime[],
      activeBooking.time
    );
    selectedTime = selectedBookingTimeFromList.selectedTime as IBookingTime;

    const preselectedTableIds = activeBooking.tables.map(({ _id }) => _id);
    try {
      const sectionsLookup = await SectionLookupService.getSectionsLookUp();
      if (sectionsLookup) {
        const { tableSelectionList, tableSelectionListWithJoins } =
          TableSelectionService.createTableSectionList(selectedTime, tables, sectionsLookup);
        dispatch(
          standbyListActions.updateStandby({
            tableSelectionList,
            tableSelectionListWithJoins,
            sectionsLookup, // object with dynamic keys
            preselectedTableIds,
          })
        );
        dispatch(bookingTimesActions.setSelectedTime(selectedTime));
        dispatch(bookingTimesActions.setAvailableTimes(mutatedAvailableTimes));
      }
    } catch (e) {
      console.warn('StandbyListHelper', 'SectionLookupService.getSectionsLookUp error', e);
      dispatch(
        errorStateActions.setErrorState({
          errorTitle: 'Sections refresh error',
          errorMessage: `There was an error while refreshing sections data. \n\n${PROBLEMS_MSG} ${REFRESH_MSG} \n\n${CONTACT_MSG}`,
          accordionDetail: getAccordionDetail(e),
        })
      );
    }
  };
